import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  BarChart3,
  Package,
  Bell,
  Sparkles,
  Truck,
  Boxes,
  Settings,
  LogOut,
  Info,
  Check,
  ChevronsUpDown,
  Users,
  ShieldCheck,
  Wallet,
  Network,
  Inbox,
  Receipt,
  Store,
  ShoppingBag,
  PackageOpen,
  Coins,
  Menu,
  X,
} from "lucide-react";
import { Brandmark, Wordmark } from "./Brand";
import { cn } from "@/lib/utils";
import { MerchantRole, useAuth } from "@/hooks/useAuth";
import { useDemoMode } from "@/hooks/useDemoMode";
import { ThemeToggle } from "./ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Item = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; badge?: number };

const workspace: Item[] = [
  { to: "/", label: "Overview", icon: LayoutGrid },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/orders", label: "Orders", icon: Package },
  { to: "/alerts", label: "Alerts", icon: Bell, badge: 4 },
  { to: "/claude", label: "Claude", icon: Sparkles },
];

const operations: Item[] = [
  { to: "/shipments", label: "Shipments", icon: Truck },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/settings", label: "Settings", icon: Settings },
];

// "Automation" stacks the five RTO-prevention / recovery surfaces. Order
// follows the order lifecycle: pre-ship quality (risk + prepaid nudge) →
// allocation (routing) → post-ship recovery (NDR) → finance (freight recon).
const automation: Item[] = [
  { to: "/risk",     label: "Address risk", icon: ShieldCheck },
  { to: "/prepaid",  label: "COD → Prepaid", icon: Wallet },
  { to: "/routing",  label: "Auto routing", icon: Network },
  { to: "/ndr",      label: "NDR recovery", icon: Inbox },
  { to: "/freight",  label: "Freight recon", icon: Receipt },
];

// "Channels" surfaces marketplace ops across Amazon / Flipkart / Meesho / Myntra.
// Order goes hub → orders → returns → settlements, mirroring the typical
// daily ops loop a marketplace seller runs.
const channels: Item[] = [
  { to: "/channels",             label: "Marketplaces",  icon: Store },
  { to: "/channels/orders",      label: "Channel orders", icon: ShoppingBag },
  { to: "/channels/returns",     label: "Returns",        icon: PackageOpen },
  { to: "/channels/settlements", label: "Settlements",    icon: Coins },
];

function Section({ title, items }: { title: string; items: Item[] }) {
  return (
    <div className="px-3">
      <div className="px-3 pb-2 pt-5 text-[10px] font-medium uppercase tracking-[0.14em] text-ink-3">
        {title}
      </div>
      <ul className="space-y-0.5">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-md px-3 py-2.5 text-[13px] transition-colors duration-[120ms]",
                  isActive
                    ? "bg-ink text-canvas"
                    : "text-ink-2 hover:bg-[hsl(var(--line-soft))]"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <it.icon className={cn("h-[15px] w-[15px]", isActive ? "opacity-90" : "opacity-70")} />
                  <span className="flex-1">{it.label}</span>
                  {it.badge ? (
                    <span className="num inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-rf px-1.5 text-[11px] text-canvas">
                      {it.badge}
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-canvas shadow-lg"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-ink/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 h-screen w-[280px] transform transition-transform duration-300 md:static md:translate-x-0 md:flex md:flex-col md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between px-5 pb-4 pt-6">
          <div className="flex items-center gap-2.5">
            <Brandmark size={28} />
            <Wordmark className="text-[20px]" />
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden flex h-8 w-8 items-center justify-center rounded-md text-ink-2 hover:bg-canvas-2"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <MerchantSwitcher />

        <nav className="flex-1 overflow-y-auto pb-4">
          <Section title="Workspace" items={workspace} />
          <Section title="Automation" items={automation} />
          <Section title="Channels" items={channels} />
          <Section title="Operations" items={operations} />
        </nav>

        <UserChip />
      </aside>
    </>
  );
}

/**
 * Sidebar workspace switcher.
 *
 * Hidden entirely when the user has zero memberships (still loading, or a
 * freshly-signed-up user mid-provision). When they have exactly one, we
 * show a static chip — no dropdown, because there's nothing to pick from.
 * With 2+ memberships, it becomes an actual switcher.
 */
function MerchantSwitcher() {
  const { memberships, currentMerchant, currentRole, switchMerchant } = useAuth();

  if (!currentMerchant || memberships.length === 0) return null;

  const multiple = memberships.length > 1;
  const roleLabel = (r: MerchantRole) => r.charAt(0).toUpperCase() + r.slice(1);

  if (!multiple) {
    return (
      <div className="px-3 pb-1">
        <div className="flex items-center gap-2 rounded-md border border-line bg-canvas-2 px-3 py-2">
          <div
            className="h-6 w-6 shrink-0 rounded-md"
            style={{ background: "linear-gradient(135deg, hsl(var(--moss)) 0%, hsl(var(--indigo)) 100%)" }}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] text-ink">{currentMerchant.name}</div>
            <div className="truncate text-[10.5px] text-ink-3">
              {currentRole ? roleLabel(currentRole) : "Member"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Switch workspace"
            className="group flex w-full items-center gap-2 rounded-md border border-line bg-canvas-2 px-3 py-2 text-left transition-colors duration-[120ms] hover:border-ink/30"
          >
            <div
              className="h-6 w-6 shrink-0 rounded-md"
              style={{ background: "linear-gradient(135deg, hsl(var(--moss)) 0%, hsl(var(--indigo)) 100%)" }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] text-ink">{currentMerchant.name}</div>
              <div className="truncate text-[10.5px] text-ink-3">
                {currentRole ? roleLabel(currentRole) : "Member"}
              </div>
            </div>
            <ChevronsUpDown className="h-3.5 w-3.5 text-ink-3 group-hover:text-ink shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[260px]">
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.14em] text-ink-3">
            Workspaces
          </DropdownMenuLabel>
          {memberships.map((m) => {
            const isActive = m.merchant_id === currentMerchant.id;
            return (
              <DropdownMenuItem
                key={m.id}
                onClick={() => !isActive && switchMerchant(m.merchant_id)}
                className="flex items-start gap-2"
              >
                <div
                  className="mt-0.5 h-6 w-6 shrink-0 rounded-md"
                  style={{ background: "linear-gradient(135deg, hsl(var(--moss)) 0%, hsl(var(--indigo)) 100%)" }}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] text-ink">{m.merchant.name}</div>
                  <div className="truncate text-[10.5px] text-ink-3">{roleLabel(m.role)}</div>
                </div>
                {isActive && <Check className="mt-1 h-3.5 w-3.5 text-ink shrink-0" />}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings/team" className="flex items-center gap-2 text-[12.5px]">
              <Users className="h-3.5 w-3.5 text-ink-3 shrink-0" /> Manage team
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function UserChip() {
  const { user, profile, signOut } = useAuth();
  const name = profile?.display_name || user?.email?.split("@")[0] || "Account";
  const brand = profile?.brand_name || "Workspace";
  const plan = profile?.plan || "Pro";
  return (
    <div className="border-t border-line p-3">
      <div className="group flex items-center gap-3 rounded-md px-2 py-2">
        <div
          className="h-9 w-9 shrink-0 rounded-full"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--accent)) 0%, hsl(var(--amber)) 100%)",
          }}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-ink">{name}</div>
          <div className="truncate text-[11px] text-ink-3">{brand} · {plan} plan</div>
        </div>
        <button
          type="button"
          onClick={() => signOut()}
          aria-label="Sign out"
          className="flex h-8 w-8 sm:h-7 sm:w-7 items-center justify-center rounded-md text-ink-3 transition-colors duration-[120ms] hover:bg-canvas-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <LogOut className="h-[14px] w-[14px] sm:h-[14px] sm:w-[14px]" />
        </button>
      </div>
    </div>
  );
}

export function Topbar() {
  const location = useLocation();
  const seg = location.pathname.split("/")[1] || "overview";
  const label = seg.charAt(0).toUpperCase() + seg.slice(1);
  const { profile, currentMerchant } = useAuth();
  const { isDemo, connectedCount, total } = useDemoMode();
  const brand =
    currentMerchant?.name?.toUpperCase() || profile?.brand_name?.toUpperCase() || "ROUTEFLOW";

  return (
    <div className="sticky top-0 z-20">
      {isDemo && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border-b border-line bg-amber/15 px-4 py-2 sm:px-6 text-[11px] sm:text-[12px] text-ink-2">
          <div className="flex items-center gap-2">
            <Info className="h-[14px] w-[14px] text-ink shrink-0" />
            <span className="break-words">
              You're viewing <span className="font-medium text-ink">demo data</span>. Connect your stack ({connectedCount}/{total}) to see real numbers.
            </span>
          </div>
          <Link
            to="/onboarding"
            className="shrink-0 rounded-md bg-ink px-3 py-1 text-[11px] sm:text-[12px] text-canvas transition-opacity duration-[120ms] hover:opacity-90"
          >
            Connect now
          </Link>
        </div>
      )}
      <header className="flex h-[58px] items-center justify-between border-b border-line bg-canvas/85 px-4 sm:px-6 backdrop-blur">
        <nav className="flex items-center gap-1 sm:gap-2 text-[12px] sm:text-[13px] text-ink-3">
          <span className="text-ink-2 truncate">{brand}</span>
          <span className="text-ink-3">/</span>
          <span className="text-ink truncate">{label}</span>
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden sm:flex items-center gap-2 rounded-full border border-line bg-canvas-2 px-3 py-1 text-[11px] sm:text-[12px] text-ink-2">
            <span className="relative flex h-2 w-2">
              <span className={cn("pulse-dot absolute inset-0 rounded-full", isDemo ? "bg-amber" : "bg-moss")} />
              <span className={cn("relative h-2 w-2 rounded-full", isDemo ? "bg-amber" : "bg-moss")} />
            </span>
            {isDemo ? "Routeflow · demo mode" : "Routeflow · synced"}
          </div>
          <IconButton label="Search">
            <SearchIcon />
          </IconButton>
          <IconButton label="Notifications">
            <BellSm />
          </IconButton>
          <IconButton label="Refresh">
            <RefreshIcon />
          </IconButton>
          <ThemeToggle />
        </div>
      </header>
    </div>
  );
}

function IconButton({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      className="flex h-9 w-9 sm:h-8 sm:w-8 items-center justify-center rounded-md text-ink-2 transition-colors duration-[120ms] hover:bg-canvas-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </button>
  );
}

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
);
const BellSm = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></svg>
);
